// @flow

import _ from 'lodash';
import validator from 'validator';

import {
  removeScriptTag,
  removeUnexpectedCharsInTerms,
  checkHasSpecialCharacters
} from 'src/helpers';

/**
 * Build redirects before saving
 *
 * @param  {array} redirects list of redirect
 *                           {
 *                             terms: <string>,
 *                             redirectTo: <string>,
 *                             enabled: <boolean>
 *                           }
 *
 * @return {array}           list of redirect
 *                           {
 *                             terms: <array>,
 *                             data: {
 *                               redirectTo: <string>
 *                             },
 *                             enabled: <boolean>
 *                           }
 */
export const buildRedirects = redirects => {
  const savedRedirects = redirects.map(redirect => {
    let { id, terms, redirectTo, enabled } = redirect;
    const split = terms.split(',');
    let parsedTerms = split.map(term => {
      return term.trim();
    });

    parsedTerms = _.filter(parsedTerms, term => {
      return !_.isEmpty(term);
    });

    redirectTo = processRedirectTo(redirectTo);

    const parsedInfo = {
      terms: parsedTerms,
      redirectTo,
      enabled
    };

    return id ? _.assign({ id }, parsedInfo) : parsedInfo;
  });

  return savedRedirects;
};

/**
 * Parse redirect after receiving from api
 *
 * @param  {array} redirects list of redirect
 *
 * @return {array}           list of parsed redirect
 */
export const parseRedirectsFromDB = redirects => {
  const parsedRedirects = redirects.map(redirect => {
    const { id, terms, enabled, redirectTo } = redirect;

    return {
      id,
      terms: terms.join(', '),
      redirectTo,
      enabled
    };
  });

  return parsedRedirects;
};

/**
 * Remove unexpected chars, script in URL and adjust protocol
 *
 * @param  {string} redirectTo URL
 *
 * @return {string}            updated URL
 */
const processRedirectTo = redirectTo => {
  let updatedRedirectTo = removeScriptTag(redirectTo);

  // Adjust protocol https:// if missing
  if (
    updatedRedirectTo &&
    updatedRedirectTo.substring(0, 8) !== 'https://' &&
    updatedRedirectTo.substring(0, 7) !== 'http://'
  ) {
    updatedRedirectTo = `https://${updatedRedirectTo}`;
  }

  return updatedRedirectTo;
};

/**
 * Check validate redirect
 *
 * @param  {array} redirects
 *                 [
 *                   {
 *
 *                     terms: <string>
 *                     redirectTo: <string>
 *                     enabled: <boolean>
 *                   }
 *                 ]
 *
 * @return {boolean}           true | false
 */
export const checkValidateRedirects = redirects => {
  for (let redirect of redirects) {
    // Search term
    if (!_.isEmpty(redirect.terms.trim())) {
      let termList = redirect.terms.split(',');
      let value = '';

      _.map(termList, term => {
        if (term.length > 128) {
          value = 'AdminHTML.Redirects.Validate.invalid_SearchTerm_Length';
          return;
        }
      });

      if (!_.isEmpty(value)) {
        return value;
      }
    }

    let checkSpecialCharactersValue = checkHasSpecialCharacters(redirect.terms);
    if (
      (_.isEmpty(redirect.terms.trim()) && !_.isEmpty(redirect.id)) ||
      checkSpecialCharactersValue
    ) {
      return 'AdminHTML.Redirects.Validate.invalid_SearchTerm_SpecialCharacter';
    }

    // Redirect to
    if (redirect.redirectTo.length > 256) {
      return 'AdminHTML.Redirects.Validate.invalid_Redirect_Length';
    }

    checkSpecialCharactersValue = checkHasSpecialCharacters(redirect.redirectTo);
    if (
      (_.isEmpty(redirect.redirectTo.trim()) && !_.isEmpty(redirect.id)) ||
      checkSpecialCharactersValue
    ) {
      return 'AdminHTML.Redirects.Validate.invalid_Redirect_SpecialCharacter';
    }

    // Check valid url
    if (!validator.isURL(redirect.redirectTo)) {
      return 'AdminHTML.Redirects.Validate.invalid_Redirect_URLFormat';
    }
  }
  return '';
};

/**
 * Check the redirects list is empty
 *
 * @param  {array} redirects
 *                 [
 *                   {
 *
 *                     terms: <string>
 *                     redirectTo: <string>
 *                     enabled: <boolean>
 *                   }
 *                 ]
 *
 * @return {boolean}  the list is empty or not
 */
export const isEmptyRedirects = redirects => {
  const redirect = _.find(redirects, item => {
    let { terms, redirectTo } = item;
    terms = removeUnexpectedCharsInTerms(terms);
    redirectTo = processRedirectTo(redirectTo);

    return !_.isEmpty(terms) && !_.isEmpty(redirectTo);
  });

  return redirect ? false : true;
};
